import React from "react";
import { Box, Container, Grid, Typography } from "@mui/material";
import { motion } from "framer-motion";
import { palette } from "../palette";
import { fadeUp, revealProps, stagger, useReducedMotion } from "../motion";
import { SmallGroup } from "../illustrations/SmallGroup";

const PERKS = [
  {
    icon: "🎯",
    title: "1:1 Focused Teaching Time",
    desc: "Every single week, your mentor sits down with you live. We remember your code, your edge cases, and where you got stuck.",
  },
  {
    icon: "☁️",
    title: "AWS & Colab Pro Included",
    desc: "Real GPU compute for every candidate so you train and deploy actual architectures instead of reading theoretical slides.",
  },
  {
    icon: "✍️",
    title: "Personalized Code Reviews",
    desc: "Custom assignments written for your target role, marked and discussed in detail with line-by-line feedback.",
  },
  {
    icon: "🛡️",
    title: "Milestone Guarantee",
    desc: "We stay in your corner until you achieve your goal—whether that is a top-tier offer letter or launching your product.",
  },
];

export const TheFifty = () => {
  const reduced = useReducedMotion();

  return (
    <Box
      component="section"
      id="the-fifty"
      aria-labelledby="fifty-heading"
      sx={{ py: { xs: 8, md: 13 }, backgroundColor: palette.bgAlt }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={{ xs: 6, md: 9 }} alignItems="center">
          <Grid item xs={12} md={6}>
            <motion.div variants={stagger(0.09)} {...revealProps(reduced)}>
              <motion.div variants={fadeUp}>
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 1,
                    px: 1.75,
                    py: 0.6,
                    borderRadius: "999px",
                    backgroundColor: palette.primarySoft,
                    color: palette.primaryHover,
                    fontWeight: 700,
                    fontSize: 12.5,
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                    mb: 2,
                  }}
                >
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      backgroundColor: palette.primaryHover,
                    }}
                  />
                  Cohort Cap: Exactly 50 Active Learners
                </Box>
              </motion.div>

              <motion.div variants={fadeUp}>
                <Typography
                  component="h2"
                  id="fifty-heading"
                  sx={{
                    color: palette.text,
                    fontWeight: 700,
                    letterSpacing: "-0.03em",
                    lineHeight: 1.1,
                    fontSize: { xs: 30, sm: 38, md: 45 },
                  }}
                >
                  We only take 50 candidates at a time
                </Typography>
              </motion.div>

              <motion.div variants={fadeUp}>
                <Typography
                  component="p"
                  sx={{
                    color: palette.textMuted,
                    fontSize: { xs: 16.5, md: 18 },
                    lineHeight: 1.7,
                    mt: 2.5,
                  }}
                >
                  Not as an artificial marketing gimmick. It is simply the physical limit of how many people our core team can sit with one-to-one, teach properly, and guide through real job interviews or startup launches.
                </Typography>
              </motion.div>

              <motion.div variants={fadeUp}>
                <Typography
                  component="p"
                  sx={{
                    color: palette.textMuted,
                    fontSize: { xs: 15.5, md: 16.5 },
                    lineHeight: 1.7,
                    mt: 2,
                  }}
                >
                  Real AWS compute credits, premium Colab subscriptions, problem sets written for one person and code reviewed line by line all take real hours per candidate. Fifty is the number where we can still do all of that properly.
                </Typography>
              </motion.div>

              {/* 4 Pillars Grid */}
              <motion.div variants={fadeUp}>
                <Grid container spacing={2} sx={{ mt: 3 }}>
                  {PERKS.map((p) => (
                    <Grid item xs={12} sm={6} key={p.title}>
                      <Box
                        sx={{
                          p: 2.2,
                          borderRadius: "16px",
                          backgroundColor: palette.paper,
                          border: `1px solid rgba(22, 24, 29, 0.06)`,
                          height: "100%",
                        }}
                      >
                        <Box sx={{ fontSize: 20, mb: 0.75 }}>{p.icon}</Box>
                        <Typography
                          sx={{
                            fontSize: 14.5,
                            fontWeight: 700,
                            color: palette.text,
                            mb: 0.5,
                          }}
                        >
                          {p.title}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 13,
                            color: palette.textMuted,
                            lineHeight: 1.5,
                          }}
                        >
                          {p.desc}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </motion.div>

              <motion.div variants={fadeUp}>
                <Typography
                  component="p"
                  sx={{
                    color: palette.text,
                    fontSize: { xs: 16, md: 17.5 },
                    lineHeight: 1.6,
                    fontWeight: 600,
                    mt: 3.5,
                  }}
                >
                  When all 50 seats are filled, we close intake until someone completes their milestones and graduates. We will tell you honestly when the next seat opens.
                </Typography>
              </motion.div>
            </motion.div>
          </Grid>

          <Grid item xs={12} md={6}>
            <SmallGroup />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default TheFifty;
